import { useAuth } from '@clerk/react'
import { List, Loader2, Shuffle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation } from '#router'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import {
  applyFilters,
  type BrowseProblem,
  createProblemsFuse,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  type SortOption,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useProblemsState } from '@/hooks/use-problems-state'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

const SORT_OPTIONS = ['difficulty', 'questionId', 'status']

const SORT_POPOVER_OPTIONS = [
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

interface MobileProblemDrawerProps {
  currentProblemSlug: string | null
  /** When set, only show problems from this list */
  listId?: string | null
}

export function MobileProblemDrawer({
  currentProblemSlug,
  listId,
}: MobileProblemDrawerProps) {
  const { navigate } = useLocation()
  const [open, setOpen] = useState(false)
  const { isSignedIn } = useAuth()

  // Fetch all problems when no listId is provided
  const { data: allProblems, isLoading: isLoadingAll } =
    trpc.problems.browse.useQuery(undefined, {
      enabled: open && !listId,
    })

  // Fetch list-specific problems when listId is provided
  const { data: listData, isLoading: isLoadingList } =
    trpc.lists.getListDetail.useQuery(
      { listId: listId ?? '' },
      { enabled: open && !!listId },
    )

  const isLoading = listId ? isLoadingList : isLoadingAll

  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: open && !!isSignedIn },
  )

  // Normalize problems from either source into BrowseProblem shape
  const problems: BrowseProblem[] = useMemo(() => {
    if (listId && listData) {
      return listData.problems.map((p) => ({
        id: p.id,
        slug: p.slug ?? '',
        title: p.title ?? '',
        externalId: p.externalId,
        difficulty: p.difficulty?.name
          ? { name: p.difficulty.name, color: p.difficulty.color ?? null }
          : null,
        topics: p.topics ?? [],
      }))
    }
    return allProblems ?? []
  }, [listId, listData, allProblems])

  const availableTopics = useMemo(
    () => (problems ? deriveAvailableTopics(problems) : []),
    [problems],
  )

  const availableDifficulties = useMemo(
    () => (problems ? deriveAvailableDifficulties(problems) : []),
    [problems],
  )

  const storageKey = listId
    ? `bctci-editor-drawer-list-${listId}`
    : 'bctci-editor-drawer'

  const {
    filters,
    sortBy,
    sortDir,
    searchQuery,
    setSearchQuery,
    setSortBy,
    setSortDir,
    matchMode,
    setMatchMode,
    showTags,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
  } = useProblemsState(storageKey, [], availableTopics, SORT_OPTIONS)

  const fuse = useMemo(() => createProblemsFuse(problems ?? []), [problems])

  const sortedProblems = useMemo(() => {
    if (!problems) return []
    const searched = searchProblems(problems, fuse, searchQuery)
    const filtered = applyFilters(
      searched,
      filters,
      matchMode,
      userProgress?.progress,
    )
    return sortProblems(
      filtered,
      sortBy as SortOption,
      sortDir,
      userProgress?.progress,
    )
  }, [
    problems,
    searchQuery,
    fuse,
    filters,
    matchMode,
    userProgress,
    sortBy,
    sortDir,
  ])

  const problemHref = (slug: string) =>
    listId
      ? `/editor?problem=${slug}&list=${listId}`
      : `/editor?problem=${slug}`

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Browse problems">
          <List className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex flex-col p-0">
        <SheetHeader className="space-y-2 border-b px-4 py-3 pr-12">
          <SheetTitle>
            <Link
              href={listId ? `/problems/lists/${listId}` : '/problems'}
              className="hover:underline"
            >
              {listId && listData ? listData.list.name : 'Problems'}
            </Link>
          </SheetTitle>
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <div className="flex items-center gap-2">
            <ProblemsFilterPopover
              filters={filters}
              matchMode={matchMode}
              onMatchModeChange={setMatchMode}
              onToggleFilter={toggleFilterValue}
              onOperatorChange={setFilterOperator}
              availableDifficulties={availableDifficulties}
              availableTopics={availableTopics}
              availableLists={[]}
              onReset={resetFilters}
            />
            <ProblemsSortPopover
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={(newSort, newDir) => {
                setSortBy?.(newSort)
                setSortDir?.(newDir)
              }}
              showTags={showTags}
              onToggleTags={() => setShowTags((prev) => !prev)}
              sortOptions={SORT_POPOVER_OPTIONS}
            />
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground p-2 transition-colors"
              aria-label="Solve a random problem"
              onClick={() => {
                if (sortedProblems.length === 0) return
                const random =
                  sortedProblems[
                    Math.floor(Math.random() * sortedProblems.length)
                  ]
                navigate(problemHref(random.slug))
                setOpen(false)
              }}
            >
              <Shuffle className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          )}
          {!isLoading && sortedProblems.length === 0 && (
            <div className="text-muted-foreground py-12 text-center text-sm">
              No problems found.
            </div>
          )}
          {sortedProblems.map((problem) => (
            <Link
              key={problem.slug}
              href={problemHref(problem.slug)}
              className={cn(
                'flex items-center gap-3 border-b px-4 py-3 text-sm transition-colors hover:bg-accent',
                problem.slug === currentProblemSlug && 'bg-accent',
              )}
              onClick={() => setOpen(false)}
            >
              <span className="text-muted-foreground w-8 shrink-0 text-right">
                {problem.externalId}
              </span>
              <span className="flex-1 truncate">{problem.title}</span>
              <span
                className={cn(
                  'shrink-0 text-xs font-medium',
                  problem.difficulty?.name === 'Easy' &&
                    'text-green-600 dark:text-green-400',
                  problem.difficulty?.name === 'Medium' &&
                    'text-yellow-600 dark:text-yellow-400',
                  problem.difficulty?.name === 'Hard' &&
                    'text-red-600 dark:text-red-400',
                )}
              >
                {problem.difficulty?.name}
              </span>
            </Link>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  )
}
